import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  UsersIcon
} from '@heroicons/react/24/outline';
import apiClient from '../../../api/axios';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isAttorney: boolean;
  isActive: boolean;
  roles: string[];
}

interface Group {
  id: string;
  name: string;
  description: string;
  groupType: string;
  isPrivate: boolean;
  isActive: boolean;
  memberCount: number;
  maxMembers?: number;
  color?: string;
  icon?: string;
}

interface UserGroupsModalProps {
  user: User;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const UserGroupsModal: React.FC<UserGroupsModalProps> = ({
  user,
  isOpen,
  onClose,
  onSuccess
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const queryClient = useQueryClient();

  // Fetch user's current groups
  const { data: userGroups, isLoading: userGroupsLoading } = useQuery({
    queryKey: ['user-groups', user.id],
    queryFn: async () => {
      const response = await apiClient.get(`/admin/groups/users/${user.id}`);
      return response.data.groups;
    },
    enabled: isOpen
  });

  // Fetch all available groups
  const { data: allGroupsData, isLoading: allGroupsLoading } = useQuery({
    queryKey: ['all-groups-for-user'],
    queryFn: async () => {
      const response = await apiClient.get('/admin/groups?limit=100');
      return response.data;
    },
    enabled: isOpen,
    refetchOnMount: 'always',  // ← Force refetch when modal opens
    staleTime: 0               // ← Consider data immediately stale
  });

  // Add user to group mutation
  const addToGroupMutation = useMutation({
    mutationFn: async (groupId: string) => {
      await apiClient.post(`/admin/groups/${groupId}/members`, {
        userId: user.id,
        groupRole: 'member',
        receiveGroupNotifications: true
      });
      return groupId; // Return groupId for cache invalidation
    },
    onSuccess: (groupId) => {
      queryClient.invalidateQueries({ queryKey: ['user-groups', user.id] });
      queryClient.invalidateQueries({ queryKey: ['group-members', groupId] }); // ← Cross-invalidate Groups tool cache
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['all-users-for-group'] }); // ← Also invalidate available users
    }
  });

  // Remove user from group mutation
  const removeFromGroupMutation = useMutation({
    mutationFn: async (groupId: string) => {
      await apiClient.delete(`/admin/groups/${groupId}/members/${user.id}`);
      return groupId; // Return groupId for cache invalidation
    },
    onSuccess: (groupId) => {
      queryClient.invalidateQueries({ queryKey: ['user-groups', user.id] });
      queryClient.invalidateQueries({ queryKey: ['group-members', groupId] }); // ← Cross-invalidate Groups tool cache
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['all-users-for-group'] }); // ← Also invalidate available users
    }
  });

  const handleAddToGroup = (groupId: string) => {
    addToGroupMutation.mutate(groupId);
  };

  const handleRemoveFromGroup = (groupId: string) => {
    if (window.confirm('Are you sure you want to remove this user from the group? This will also remove them from the corresponding Keycloak group.')) {
      removeFromGroupMutation.mutate(groupId);
    }
  };

  const getTypeBadge = (groupType: string) => {
    const typeLabels: { [key: string]: string } = {
      'department': 'Department',
      'practice_team': 'Practice Team',
      'project_team': 'Project Team',
      'administrative': 'Administrative',
      'client_team': 'Client Team',
      'custom': 'Custom'
    };
    
    return (
      <Badge variant="outline">
        {typeLabels[groupType] || groupType.charAt(0).toUpperCase() + groupType.slice(1)}
      </Badge>
    );
  };

  if (!isOpen) return null;

  const userGroupIds = userGroups?.map((g: Group) => g.id) || [];
  const availableGroups = allGroupsData?.groups?.filter((group: Group) => 
    !userGroupIds.includes(group.id) && 
    group.isActive &&
    (!searchTerm || group.name.toLowerCase().includes(searchTerm.toLowerCase()))
  ) || [];

  const filteredUserGroups = userGroups?.filter((group: Group) =>
    !searchTerm || group.name.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Manage Groups for {user.firstName} {user.lastName}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Add or remove user from groups. Changes will sync with Keycloak automatically.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search groups..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Current Groups */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
              <UsersIcon className="h-5 w-5 mr-2" />
              Current Groups ({filteredUserGroups.length})
            </h3>
            
            {userGroupsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-lex-accent"></div>
              </div>
            ) : filteredUserGroups.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                {searchTerm ? 'No groups match your search' : 'User is not in any groups'}
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {filteredUserGroups.map((group: Group) => (
                  <div
                    key={group.id}
                    className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-lg"
                  >
                    <div className="flex items-center flex-1">
                      {group.icon && (
                        <span className="mr-2 text-lg">{group.icon}</span>
                      )}
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {group.name}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          {getTypeBadge(group.groupType)}
                          <span className="text-xs text-gray-500">
                            {group.memberCount} members
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveFromGroup(group.id)}
                      disabled={removeFromGroupMutation.isPending}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Available Groups */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
              <PlusIcon className="h-5 w-5 mr-2" />
              Available Groups ({availableGroups.length})
            </h3>
            
            {allGroupsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-lex-accent"></div>
              </div>
            ) : availableGroups.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                {searchTerm ? 'No available groups match your search' : 'User is already in all available groups'}
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {availableGroups.map((group: Group) => (
                  <div
                    key={group.id}
                    className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-lg"
                  >
                    <div className="flex items-center flex-1">
                      {group.icon && (
                        <span className="mr-2 text-lg">{group.icon}</span>
                      )}
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {group.name}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          {getTypeBadge(group.groupType)}
                          <span className="text-xs text-gray-500">
                            {group.memberCount} members
                            {group.maxMembers && ` / ${group.maxMembers} max`}
                          </span>
                        </div>
                        {group.description && (
                          <div className="text-xs text-gray-500 mt-1 truncate">
                            {group.description}
                          </div>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAddToGroup(group.id)}
                      disabled={addToGroupMutation.isPending || !!(group.maxMembers && group.memberCount >= group.maxMembers)}
                      className="text-green-600 hover:text-green-700 hover:bg-green-50"
                    >
                      <PlusIcon className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button 
            onClick={() => {
              onSuccess();
              onClose();
            }}
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}; 